import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { smartPasteHandler } from "@/lib/smartPaste";
import { X, Users, Plus, Trash, PencilSimple, MapPin, Star, User as UserIcon, Phone, Envelope } from "@phosphor-icons/react";

/**
 * Edit an existing requirement — mirrors the create form but pre-fills every
 * field from the job doc and PUTs to `/jobs/{id}`. Focused on the fields
 * recruiters most often need to correct after creation:
 *   title / description / requirements / status / screening threshold /
 *   client / SPOCs / per-job referral bounty.
 *
 * Callback: `onSaved(updatedJob)` — the parent refreshes its state.
 */
export default function EditJobModal({ open, job, onClose, onSaved }) {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [clients, setClients] = useState([]);
    const [clientLocations, setClientLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        // Load the workspace client list so recruiters can attach/change
        // the client for this JD. Silent-fail on error.
        api.get("/clients")
            .then((r) => setClients(r.data || []))
            .catch(() => {});
    }, [open]);

    useEffect(() => {
        if (!open || !job) return;
        setErr("");
        setForm({
            title: job.title || "",
            client_id: job.client_id || "",
            hiring_manager_id: job.hiring_manager_id || "",
            primary_location_id: job.primary_location_id || "",
            primary_spoc_id: job.primary_spoc_id || "",
            work_mode: job.work_mode || "Onsite",
            department: job.department || "",
            location: job.location || "",
            employment_type: job.employment_type || "Full-time",
            seniority: job.seniority || "Mid",
            salary_range: job.salary_range || "",
            description: job.description || "",
            requirements: job.requirements || "",
            status: job.status || "Open",
            screening_pass_threshold: job.screening_pass_threshold ?? 3.5,
            spocs: (job.spocs || []).map((s) => ({
                name: s.name || "",
                email: s.email || "",
                phone: s.phone || "",
            })),
            referral_eligible: !!job.referral_eligible,
            referral_bounty_amount:
                job.referral_bounty_amount != null ? String(job.referral_bounty_amount) : "",
            referral_bounty_currency: job.referral_bounty_currency || "INR",
        });
    }, [open, job]);

    // V1.5.5 Phase 2 — Fetch locations whenever the client selection
    // changes inside the edit form. Preserves the existing primary_*_id
    // pointers if they still resolve to a location on the new client.
    useEffect(() => {
        if (!open) return;
        const cid = form?.client_id;
        if (!cid) {
            setClientLocations([]);
            return;
        }
        let cancelled = false;
        setLocationsLoading(true);
        api.get(`/clients/${cid}/locations`)
            .then((r) => {
                if (cancelled) return;
                const locs = r.data || [];
                setClientLocations(locs);
                setForm((f) => {
                    if (!f) return f;
                    const stillValid = locs.some((l) => l.id === f.primary_location_id);
                    if (stillValid) return f;
                    const primary = locs.find((l) => l.is_primary) || locs[0];
                    if (!primary) {
                        return { ...f, primary_location_id: "", primary_spoc_id: "" };
                    }
                    const primarySpoc =
                        (primary.spocs || []).find((s) => s.is_primary) ||
                        (primary.spocs || [])[0];
                    return {
                        ...f,
                        primary_location_id: primary.id,
                        primary_spoc_id: primarySpoc ? primarySpoc.id : "",
                    };
                });
            })
            .catch(() => { if (!cancelled) setClientLocations([]); })
            .finally(() => { if (!cancelled) setLocationsLoading(false); });
        return () => { cancelled = true; };
    }, [open, form?.client_id]);

    const selectedLocation = useMemo(
        () => clientLocations.find((l) => l.id === form?.primary_location_id) || null,
        [clientLocations, form?.primary_location_id],
    );
    const selectedLocationSpocs = selectedLocation?.spocs || [];

    if (!open || !form) return null;

    const addSpoc = () =>
        setForm((f) => ({ ...f, spocs: [...(f.spocs || []), { name: "", email: "", phone: "" }] }));
    const updateSpoc = (i, k, v) =>
        setForm((f) => ({
            ...f,
            spocs: (f.spocs || []).map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
        }));
    const removeSpoc = (i) =>
        setForm((f) => ({ ...f, spocs: (f.spocs || []).filter((_, idx) => idx !== i) }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            const spocs = (form.spocs || [])
                .filter((s) => (s.name || "").trim())
                .map((s) => ({
                    name: s.name.trim(),
                    email: (s.email || "").trim() || null,
                    phone: (s.phone || "").trim() || "",
                }));
            const payload = {
                ...form,
                client_id: form.client_id || null,
                hiring_manager_id: form.hiring_manager_id || null,
                primary_location_id: form.primary_location_id || null,
                primary_spoc_id: form.primary_spoc_id || null,
                work_mode: form.work_mode || "Onsite",
                screening_pass_threshold: Number(form.screening_pass_threshold) || 3.5,
                spocs,
                referral_eligible: !!form.referral_eligible,
                referral_bounty_amount: form.referral_eligible
                    ? Number(form.referral_bounty_amount) || 0
                    : null,
                referral_bounty_currency: form.referral_eligible
                    ? form.referral_bounty_currency || "INR"
                    : null,
            };
            const r = await api.put(`/jobs/${job.id}`, payload);
            toast.success("Requirement updated.");
            onSaved?.(r.data);
            onClose?.();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="edit-job-modal"
            className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-6"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-3xl my-4 border border-[#E5E5E5]"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#F5F5F5]">
                    <div className="flex items-center gap-2">
                        <PencilSimple size={16} weight="bold" className="text-cobalt" />
                        <p className="font-heading text-lg font-bold tracking-tight">
                            Edit requirement — <span className="text-[#525252]">{job.code || job.title}</span>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="edit-job-close"
                        className="p-1.5 hover:bg-[#F5F5F5] text-[#525252]"
                        aria-label="Close"
                    >
                        <X size={16} weight="bold" />
                    </button>
                </div>

                <form
                    data-testid="edit-job-form"
                    onSubmit={submit}
                    className="px-5 py-5 space-y-4 max-h-[80vh] overflow-y-auto"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Title">
                            <input
                                data-testid="edit-title"
                                value={form.title}
                                required
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                className={inputCls}
                            />
                        </Field>
                        <Field label="Status">
                            <select
                                data-testid="edit-status"
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                className={inputCls}
                            >
                                <option>Open</option>
                                <option>Paused</option>
                                <option>Closed</option>
                                <option>Inactive</option>
                            </select>
                        </Field>
                        <Field label="Client">
                            <select
                                data-testid="edit-client_id"
                                value={form.client_id || ""}
                                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                                className={inputCls}
                            >
                                <option value="">— Internal / no client —</option>
                                {clients
                                    .filter((c) => c.status !== "archived")
                                    .map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                            </select>
                            {!form.client_id && (
                                <p
                                    data-testid="edit-no-client-warning"
                                    className="mt-2 text-[11px] leading-relaxed text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] p-2"
                                >
                                    Without a client, testimonials, white-label
                                    career pages, and share links won&apos;t work
                                    for this requirement.
                                </p>
                            )}
                        </Field>
                        <Field label="Work mode">
                            <select
                                data-testid="edit-work_mode"
                                value={form.work_mode || "Onsite"}
                                onChange={(e) => setForm({ ...form, work_mode: e.target.value })}
                                className={inputCls}
                            >
                                <option value="Onsite">Onsite</option>
                                <option value="Hybrid">Hybrid</option>
                                <option value="Remote">Remote</option>
                            </select>
                            <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                Physical office comes from the linked Client Office above.
                            </p>
                        </Field>
                        <Field label="Salary range">
                            <input
                                data-testid="edit-salary"
                                value={form.salary_range}
                                onChange={(e) => setForm({ ...form, salary_range: e.target.value })}
                                className={inputCls}
                            />
                        </Field>
                        <Field label="Employment type">
                            <select
                                data-testid="edit-employment"
                                value={form.employment_type}
                                onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                                className={inputCls}
                            >
                                <option>Full-time</option>
                                <option>Part-time</option>
                                <option>Contract</option>
                                <option>Internship</option>
                            </select>
                        </Field>
                        <Field label="Seniority">
                            <select
                                data-testid="edit-seniority"
                                value={form.seniority}
                                onChange={(e) => setForm({ ...form, seniority: e.target.value })}
                                className={inputCls}
                            >
                                <option>Junior</option>
                                <option>Mid</option>
                                <option>Senior</option>
                                <option>Lead</option>
                                <option>Principal</option>
                            </select>
                        </Field>
                    </div>

                    {/* V1.5.5 Phase 2 — Attach Client Office + SPOC to this JD */}
                    {form.client_id && (
                        <div
                            data-testid="edit-location-spoc-block"
                            className="bg-[#F8FAFC] border border-[#E5E5E5] p-4 space-y-3"
                        >
                            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
                                <MapPin size={12} weight="fill" className="text-cobalt" />
                                Client office & SPOC
                            </div>
                            {locationsLoading ? (
                                <p className="text-[11px] text-[#A3A3A3]">Loading client locations…</p>
                            ) : clientLocations.length === 0 ? (
                                <p className="text-[11px] leading-relaxed text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] p-2">
                                    This client has no offices set up yet. Add them on
                                    the Client page to attach an office + SPOC here.
                                </p>
                            ) : (
                                <>
                                    <div>
                                        <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5 block">
                                            Office location
                                        </label>
                                        <select
                                            data-testid="edit-primary_location_id"
                                            value={form.primary_location_id || ""}
                                            onChange={(e) => {
                                                const lid = e.target.value;
                                                const loc = clientLocations.find((l) => l.id === lid);
                                                const spoc =
                                                    loc && ((loc.spocs || []).find((s) => s.is_primary) || (loc.spocs || [])[0]);
                                                setForm((f) => ({
                                                    ...f,
                                                    primary_location_id: lid,
                                                    primary_spoc_id: spoc ? spoc.id : "",
                                                }));
                                            }}
                                            className={inputCls}
                                        >
                                            <option value="">— Select office —</option>
                                            {clientLocations.map((l) => {
                                                const sameCity = (l.city || "").trim().toLowerCase() === (l.label || "").trim().toLowerCase();
                                                return (
                                                    <option key={l.id} value={l.id}>
                                                        {l.label}
                                                        {l.city && !sameCity ? ` · ${l.city}` : ""}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    {selectedLocation && (
                                        <div
                                            data-testid="edit-selected-location-preview"
                                            className="border border-[#E5E5E5] bg-white p-3"
                                        >
                                            <div className="flex items-start gap-2">
                                                <MapPin size={14} weight="fill" className="text-cobalt mt-0.5 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-ink flex items-center gap-2">
                                                        {selectedLocation.label}
                                                        {selectedLocation.is_primary && (
                                                            <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5">
                                                                <Star size={9} weight="fill" /> Primary
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="text-[11px] text-[#525252] mt-0.5">
                                                        {selectedLocation.address_line_1}
                                                        {selectedLocation.address_line_2 ? `, ${selectedLocation.address_line_2}` : ""}
                                                        {selectedLocation.city ? `, ${selectedLocation.city}` : ""}
                                                        {selectedLocation.state ? `, ${selectedLocation.state}` : ""}
                                                        {selectedLocation.pincode ? ` ${selectedLocation.pincode}` : ""}
                                                    </p>
                                                    {selectedLocation.google_maps_url && (
                                                        <a
                                                            href={selectedLocation.google_maps_url}
                                                            target="_blank" rel="noopener noreferrer"
                                                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-cobalt hover:underline"
                                                        >
                                                            Open in Google Maps ↗
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                            {selectedLocationSpocs.length > 0 ? (
                                                <div className="mt-3 pt-3 border-t border-[#F5F5F5]">
                                                    <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1.5 block">
                                                        Point of contact (SPOC)
                                                    </label>
                                                    <select
                                                        data-testid="edit-primary_spoc_id"
                                                        value={form.primary_spoc_id || ""}
                                                        onChange={(e) =>
                                                            setForm({ ...form, primary_spoc_id: e.target.value })
                                                        }
                                                        className={inputCls}
                                                    >
                                                        <option value="">— No SPOC —</option>
                                                        {selectedLocationSpocs.map((s) => (
                                                            <option key={s.id} value={s.id}>
                                                                {s.name} · {s.designation}
                                                                {s.is_primary ? "  (Primary)" : ""}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {form.primary_spoc_id && (() => {
                                                        const spoc = selectedLocationSpocs.find((s) => s.id === form.primary_spoc_id);
                                                        if (!spoc) return null;
                                                        return (
                                                            <div className="mt-2 text-[11px] text-[#525252] flex flex-wrap items-center gap-3 pl-1">
                                                                <span className="inline-flex items-center gap-1 font-bold text-ink">
                                                                    <UserIcon size={11} weight="fill" /> {spoc.name}
                                                                </span>
                                                                <span className="inline-flex items-center gap-1">
                                                                    <Phone size={10} weight="fill" /> {spoc.phone}
                                                                </span>
                                                                <span className="inline-flex items-center gap-1">
                                                                    <Envelope size={10} weight="fill" /> {spoc.email}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <p className="mt-2 pt-2 border-t border-[#F5F5F5] text-[10px] text-[#A3A3A3]">
                                                    No SPOCs configured for this office yet.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <Field label="Job description">
                        <textarea
                            data-testid="edit-description"
                            value={form.description}
                            rows={5}
                            required
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            onPaste={smartPasteHandler((v) => setForm((f) => ({ ...f, description: v })))}
                            className={`${inputCls} whitespace-pre-wrap`}
                        />
                    </Field>

                    <Field label="Key requirements">
                        <textarea
                            data-testid="edit-requirements"
                            value={form.requirements}
                            rows={4}
                            required
                            onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                            onPaste={smartPasteHandler((v) => setForm((f) => ({ ...f, requirements: v })))}
                            className={`${inputCls} whitespace-pre-wrap`}
                        />
                    </Field>

                    {/* Extra SPOCs — one-off contacts specific to this
                        requirement. Primary hiring contact comes from the
                        linked Client Office (V1.5.5). */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] inline-flex items-center gap-1.5">
                                <Users size={12} weight="bold" /> Extra SPOCs
                            </p>
                            <button
                                type="button"
                                onClick={addSpoc}
                                data-testid="edit-add-spoc"
                                className="text-[10px] font-black uppercase tracking-[0.15em] text-cobalt hover:text-cobalt-dark inline-flex items-center gap-1"
                            >
                                <Plus size={11} weight="bold" /> Add extra SPOC
                            </button>
                        </div>
                        {(form.spocs || []).length === 0 && (
                            <p className="text-[11px] text-[#A3A3A3]">
                                No extra SPOCs yet. Primary contact is already picked from the linked Client Office above.
                            </p>
                        )}
                        <div className="space-y-2">
                            {(form.spocs || []).map((s, idx) => (
                                <div
                                    key={idx}
                                    data-testid={`edit-spoc-row-${idx}`}
                                    className="grid grid-cols-1 sm:grid-cols-[1.1fr_1.3fr_1fr_auto] gap-2 items-start"
                                >
                                    <input
                                        data-testid={`edit-spoc-name-${idx}`}
                                        placeholder="Name"
                                        value={s.name}
                                        onChange={(e) => updateSpoc(idx, "name", e.target.value)}
                                        className={inputCls}
                                    />
                                    <input
                                        data-testid={`edit-spoc-email-${idx}`}
                                        type="email"
                                        placeholder="email@client.com"
                                        value={s.email}
                                        onChange={(e) => updateSpoc(idx, "email", e.target.value)}
                                        className={inputCls}
                                    />
                                    <input
                                        data-testid={`edit-spoc-phone-${idx}`}
                                        type="tel"
                                        placeholder="+91 98…"
                                        value={s.phone}
                                        onChange={(e) => updateSpoc(idx, "phone", e.target.value)}
                                        className={inputCls}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeSpoc(idx)}
                                        data-testid={`edit-remove-spoc-${idx}`}
                                        aria-label="Remove SPOC"
                                        className="text-[#DC2626] hover:bg-[#FEF2F2] w-9 h-9 flex items-center justify-center border border-[#E5E5E5] hover:border-[#DC2626]"
                                    >
                                        <Trash size={14} weight="bold" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Referral bounty */}
                    <div className="border border-[#E5E5E5] p-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="edit-referral-eligible"
                                checked={!!form.referral_eligible}
                                onChange={(e) =>
                                    setForm({ ...form, referral_eligible: e.target.checked })
                                }
                                className="mt-1 accent-cobalt"
                            />
                            <div className="flex-1">
                                <p className="font-bold text-ink text-sm">
                                    This role is referral-bounty eligible
                                </p>
                                <p className="text-[11px] text-[#525252] mt-0.5">
                                    Overrides the workspace default when a candidate on this role
                                    is hired.
                                </p>
                            </div>
                        </label>
                        {form.referral_eligible && (
                            <div className="grid grid-cols-[1fr_120px] gap-2 mt-3 pl-7">
                                <input
                                    data-testid="edit-referral-amount"
                                    type="number"
                                    min="0"
                                    step="100"
                                    placeholder="Bounty amount"
                                    value={form.referral_bounty_amount}
                                    onChange={(e) =>
                                        setForm({ ...form, referral_bounty_amount: e.target.value })
                                    }
                                    required
                                    className={inputCls}
                                />
                                <select
                                    data-testid="edit-referral-currency"
                                    value={form.referral_bounty_currency}
                                    onChange={(e) =>
                                        setForm({ ...form, referral_bounty_currency: e.target.value })
                                    }
                                    className={inputCls}
                                >
                                    <option value="INR">INR ₹</option>
                                    <option value="USD">USD $</option>
                                    <option value="EUR">EUR €</option>
                                    <option value="GBP">GBP £</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {err && (
                        <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3">
                            {err}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F5F5F5]">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="edit-job-save"
                            className="btn-gradient px-5 py-2.5 text-xs font-black uppercase tracking-wider disabled:opacity-50"
                        >
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const inputCls =
    "w-full bg-white border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt";

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5 block">
                {label}
            </label>
            {children}
        </div>
    );
}
